/* Sing (or play) a target pitch and hold it inside the tolerance for a while. */
import { useEffect, useRef } from 'react';
import { onMic } from '../../../core/mic';
import { noteParts } from '../../../core/theory';
import { Btn } from '../../../ui/Button';
import { Gauge, type GaugeHandle } from '../../../ui/Gauge';
import { PitchGraph, type PitchGraphHandle } from '../../../ui/PitchGraph';
import { Ring, type RingHandle } from '../../../ui/widgets';
import type { HoldQuestion, QuestionApi } from '../types';

export function HoldArea({ q, api }: { q: HoldQuestion; api: QuestionApi }) {
  const tol = q.tol || 30;
  const holdSec = q.hold || 1.0;
  const timeout = q.timeout || 12;
  const flex = q.flex !== false;
  const gauge = useRef<GaugeHandle>(null);
  const graph = useRef<PitchGraphHandle>(null);
  const ring = useRef<RingHandle>(null);
  const ringTxt = useRef<HTMLDivElement>(null);
  const mic = api.input === 'mic';

  // biome-ignore lint/correctness/useExhaustiveDependencies: one listener per mounted question
  useEffect(() => {
    if (!mic) return;
    const p = noteParts(q.target);
    graph.current?.setBand(q.target, tol);
    gauge.current?.update({ active: false, main: p.main, sub: p.sub });
    let held = 0;
    let lastT: number | null = null;
    let firstVoice: number | null = null;
    const devs: number[] = [];
    const setRingTxt = (t: string) => {
      if (ringTxt.current) ringTxt.current.textContent = t;
    };
    const finish = (ok: boolean) => {
      ring.current?.set(ok ? 1 : held / holdSec);
      setRingTxt(ok ? 'Tamam' : 'Süre doldu');
      const recent = devs.slice(-40);
      const avg = recent.length ? recent.reduce((a, b) => a + b, 0) / recent.length : 0;
      api.done(ok, { avgCents: avg });
    };
    return onMic('frame', (f) => {
      graph.current?.setNow(f.t);
      if (api.isDone()) return;
      if (f.gated) {
        lastT = null;
        graph.current?.push(f.t, null);
        setRingTxt('Dinle');
        return;
      }
      const dt = lastT == null ? 0 : Math.min(0.1, f.t - lastT);
      lastT = f.t;
      if (!f.voiced || f.rms < f.gate * 1.3 || f.midi == null) {
        held = Math.max(0, held - dt * 1.2);
        ring.current?.set(held / holdSec);
        graph.current?.push(f.t, null);
        gauge.current?.update({ active: false });
        setRingTxt(held > 0 ? 'Devam' : 'Söyle');
        if (firstVoice != null && f.t - firstVoice > timeout) finish(false);
        return;
      }
      let m = f.midi;
      if (flex) {
        while (m - q.target > 6) m -= 12;
        while (q.target - m > 6) m += 12;
      }
      const cents = (m - q.target) * 100;
      if (firstVoice == null) firstVoice = f.t;
      devs.push(cents);
      const ok = Math.abs(cents) <= tol;
      held = ok ? held + dt : Math.max(0, held - dt * 1.5);
      ring.current?.set(held / holdSec);
      setRingTxt(ok ? 'Tut!' : cents < 0 ? 'Yukarı' : 'Aşağı');
      graph.current?.push(f.t, m, ok ? 'good' : Math.abs(cents) <= tol * 2 ? 'warn' : 'bad');
      gauge.current?.update({ active: true, main: p.main, sub: p.sub, cents, hz: f.freq, tol });
      if (held >= holdSec) finish(true);
      else if (f.t - firstVoice > timeout) finish(false);
    });
  }, [api]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: set once per mounted question
  useEffect(() => {
    if (q.explain || !mic) return;
    api.setExplain((ok, d) => {
      if (d?.self) return '';
      const a = Math.round(d?.avgCents || 0);
      if (ok) return `Ortalama sapma: ${a > 0 ? '+' : ''}${a} sent.`;
      return a < -tol
        ? `Genelde biraz **pes** kaldın (${a} sent). Sesi biraz yukarı taşı.`
        : a > tol
          ? `Genelde biraz **tiz** kaldın (+${a} sent).`
          : 'Sesi daha uzun süre sabit tutmayı dene.';
    });
  }, [api]);

  if (!mic)
    return (
      <div className="selfcheck">
        <p>Mikrofon kapalı: söyledikten sonra kendin değerlendir.</p>
        <div className="sc-actions">
          <Btn kind="primary" icon="check" onClick={() => api.done(true, { self: true })}>
            Tuttum
          </Btn>
          <Btn kind="ghost" onClick={() => api.done(false, { self: true })}>
            Zorlandım
          </Btn>
        </div>
      </div>
    );
  const p = noteParts(q.target);
  return (
    <>
      <div className="hold-top">
        <Gauge ref={gauge} main={p.main} sub={p.sub} />
        <div className="hold-ring">
          <Ring ref={ring} frac={0} size={74} />
          <div className="hold-txt" ref={ringTxt}>
            Söyle
          </div>
        </div>
      </div>
      <PitchGraph ref={graph} lo={q.target - 6} hi={q.target + 6} height={190} seconds={5} nowFrac={0.92} />
    </>
  );
}
